import { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_BASE}/analytics/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  if (!data) {
    return <div>Error loading dashboard</div>;
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

      {/* Today's Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <MetricCard title="Emails Sent" value={data.today.sends} />
        <MetricCard title="Opens" value={data.today.opens} />
        <MetricCard title="Clicks" value={data.today.clicks} />
        <MetricCard title="MQLs" value={data.today.mqls} />
        <MetricCard title="Revenue" value={`$${data.today.revenue.toFixed(2)}`} />
      </div>

      {/* Pipeline Status */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Pipeline Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Total Leads</p>
            <p className="text-2xl font-bold">{data.pipeline.totalLeads}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Ready to Email</p>
            <p className="text-2xl font-bold">{data.pipeline.readyLeads}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Campaigns</p>
            <p className="text-2xl font-bold">{data.pipeline.activeCampaigns}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Converted</p>
            <p className="text-2xl font-bold">{data.pipeline.totalConverted}</p>
          </div>
        </div>
      </div>

      {/* Domain Health */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Domain Health</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {data.domainHealth.map((domain) => (
            <div key={domain.domain_name} className="border rounded p-3">
              <p className="text-sm font-medium">{domain.domain_name}</p>
              <p className={`text-lg font-bold ${
                domain.health_score >= 90 ? 'text-green-600' :
                domain.health_score >= 80 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {domain.health_score}%
              </p>
              <p className="text-xs text-gray-500">{domain.emails_sent_today}/{domain.daily_limit} today</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent MQLs */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Recent MQLs</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.recentMQLs.map((mql) => (
                <tr key={mql.id}>
                  <td className="px-4 py-3 text-sm">{mql.first_name} {mql.last_name}</td>
                  <td className="px-4 py-3 text-sm">{mql.company_name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      mql.grade === 'A+' ? 'bg-green-100 text-green-800' :
                      mql.grade === 'A' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {mql.grade}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">${parseFloat(mql.total_revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-sm text-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

